import { createPendientesPage, ESTATUS2_SELECT_FIELD_OPTIONS } from "./PendientesLocalPage";
import { parseFechaPedido } from "./baseDatosColumns";

const CANCELACIONES_DATE_FILTER_FIELD = '__CANCELACIONES_DATE_FILTER__';
const CANCELACIONES_REACT_CUTOFF_DATE = new Date(2026, 5, 1);
const ESTATUS_CANCELACION_OPTIONS = Object.freeze([
  'RECUPERAR PIEZA',
  'RECUPERAR EFECTIVO',
  'RECUPERADO',
  'NO SURTIDO',
  'DEVOLUCION A PROVEEDOR',
  'REASIGNADO',
  ' '
]);

const hasValue = (value) => {
  if (value === null || typeof value === 'undefined') return false;
  return String(value).trim() !== '';
};

const normalizeStatusValue = (value) =>
  (value ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const isMultiasistenciaAseguradora = (value) => normalizeStatusValue(value).includes('multiasistencia');
const isQualitasXnuupAseguradora = (value) => ['qualitas', 'xnuup', 'xnuub'].some((item) => normalizeStatusValue(value).includes(item));
const isQualitasPortalCanceladoRow = (row) => isQualitasXnuupAseguradora(row?.ASEGURADORA) && normalizeStatusValue(row?.ESTATUS_PORTAL_Q) === "cancelado";
const isQualitasBackOrderRCanceladoRow = (row) => isQualitasXnuupAseguradora(row?.ASEGURADORA) && normalizeStatusValue(row?.BACK_ORDER).endsWith('-r') && normalizeStatusValue(row?.NUEVO_ESTATUS) === 'cancelado';

const hasCambioStatus = (row) => {
  const normalizedEstatus2 = normalizeStatusValue(row?.ESTATUS2);
  return normalizedEstatus2.includes('cambio');
};

const isValeManualCanceladoRow = (row) => normalizeStatusValue(row?.ESTATUS) === 'vale manual' && normalizeStatusValue(row?.ESTATUS2) === 'cancelado';
const hasEnTransitoStatus = (row) => {
  const normalizedEstatus2 = normalizeStatusValue(row?.ESTATUS2);
  const normalizedNuevoEstatus = normalizeStatusValue(row?.NUEVO_ESTATUS);
  return normalizedEstatus2 === 'en transito' || normalizedNuevoEstatus === 'en transito';
};

const hasEnProcesoDevolucionStatus = (row) => normalizeStatusValue(row?.NUEVO_ESTATUS).replace(/\s+/g, " ") === "en proceso de devolucion";
const hasNoRecibidoStatus = (row) => normalizeStatusValue(row?.NUEVO_ESTATUS).replace(/\s+/g, " ") === "no recibido";
const shouldForceRecuperarPieza = (value) => {
  const raw = value == null ? '' : String(value).trim();
  if (raw === '') return true;
  return /\d/.test(raw);
};

const getCancelacionStatus = (params) => {
  const value = params?.data?.DIAS_ENTREGADO1;
  return value == null ? '' : String(value).trim().toUpperCase();
};

const isFinalCancelacionStatus = (value) => {
  const normalized = normalizeStatusValue(value);
  return normalized === 'recuperado'
    || normalized === 'no surtido'
    || normalized === 'devolucion a proveedor';
};

const isPendingRecoverStatus = (value) => normalizeStatusValue(value).includes('recuperar');

const isCambioPorRecuperarStatus = (row) => (
  hasCambioStatus(row)
  && !isFinalCancelacionStatus(row?.DIAS_ENTREGADO1)
  && isPendingRecoverStatus(row?.DIAS_ENTREGADO1)
);

const hasZeroDiasCounter = (row) => {
  const normalized = normalizeStatusValue(row?.DIAS_ENTREGADO2).replace(/\s+/g, ' ');
  return normalized === '0 dias' || normalized === '0 dia';
};

const hasBackOrderDate = (value) => {
  const raw = value == null ? '' : String(value).trim();
  if (!raw || raw === '0' || raw === '0.0') return false;
  const hasDateShape = /\d{1,2}[/-]\d{1,2}[/-]\d{4}/.test(raw);
  if (!hasDateShape) return false;
  const parsed = parseFechaPedido(raw);
  return parsed instanceof Date && !Number.isNaN(parsed.getTime());
};

const cancelacionRowClassRules = {
  'row-cancelacion-cambio': (params) => {
    if (hasZeroDiasCounter(params?.data)) return false;
    return hasCambioStatus(params?.data);
  },
  'row-cancelacion-recuperado': (params) => {
    if (hasZeroDiasCounter(params?.data)) return false;
    if (hasCambioStatus(params?.data)) return false;
    const normalized = getCancelacionStatus(params);
    return normalized === 'RECUPERADO' || normalized === 'NO SURTIDO';
  },
  'row-cancelacion-rojo': (params) => {
    if (hasZeroDiasCounter(params?.data)) return false;
    if (hasCambioStatus(params?.data)) return false;
    const normalized = getCancelacionStatus(params);
    return normalized.includes('RECUPERAR') && normalized !== 'RECUPERADO';
  }
};

const filterCancelacionesRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  const soloCanceladas = rows
    .filter((row) => {
      if (isValeManualCanceladoRow(row)) return true;
      if (hasCambioStatus(row)) return true;
      if (hasEnTransitoStatus(row)) return true;
      if (hasEnProcesoDevolucionStatus(row)) return true;
      if (hasNoRecibidoStatus(row)) return true;
      if (isQualitasBackOrderRCanceladoRow(row)) return true;
      if (isQualitasPortalCanceladoRow(row)) return true;

      const fechaCancelacion = parseFechaPedido(row?.FECHA_CANCELACION_DE_PIEZA);
      if (fechaCancelacion && fechaCancelacion >= CANCELACIONES_REACT_CUTOFF_DATE) return true;

      return false;
    })
    .map((row) => {
      const fechaFiltro = row?.FECHA_CANCELACION_DE_PIEZA || ((hasCambioStatus(row) || hasEnTransitoStatus(row) || hasEnProcesoDevolucionStatus(row) || hasNoRecibidoStatus(row) || isValeManualCanceladoRow(row)) ? row?.FECHA_PEDIDO : '');
      const needsRecuperarPieza = hasCambioStatus(row) && shouldForceRecuperarPieza(row?.DIAS_ENTREGADO1);
      const nextRow = needsRecuperarPieza
        ? { ...row, DIAS_ENTREGADO1: 'RECUPERAR PIEZA' }
        : row;

      if (!hasValue(fechaFiltro)) return nextRow;
      return { ...nextRow, [CANCELACIONES_DATE_FILTER_FIELD]: fechaFiltro };
    });

  return [...soloCanceladas].sort((a, b) => {
    const cambioA = hasCambioStatus(a);
    const cambioB = hasCambioStatus(b);
    if (cambioA !== cambioB) return cambioA ? -1 : 1;

    const fechaCancelacionA = parseFechaPedido(a?.[CANCELACIONES_DATE_FILTER_FIELD] || a?.FECHA_CANCELACION_DE_PIEZA || a?.FECHA_PEDIDO);
    const fechaCancelacionB = parseFechaPedido(b?.[CANCELACIONES_DATE_FILTER_FIELD] || b?.FECHA_CANCELACION_DE_PIEZA || b?.FECHA_PEDIDO);
    const diffCancelacion = fechaCancelacionB - fechaCancelacionA;
    if (diffCancelacion !== 0) return diffCancelacion;

    // Desempata por fecha de pedido para mantener un orden estable.
    return parseFechaPedido(b?.FECHA_PEDIDO) - parseFechaPedido(a?.FECHA_PEDIDO);
  });
};

const CancelacionesPage = createPendientesPage({
  pageTitle: "Cancelaciones",
  pageSubtitle: "Vista general de registros con cancelaciones.",
  filterBaseRows: filterCancelacionesRows,
  exportFilePrefix: "cancelaciones",
  exportSheetName: "Cancelaciones",
  columnVisibilityStorageKey: "baseDatosColumnVisibility-cancelaciones",
  columnFieldOrder: [
    'ESTATUS_LOCAL',
    'ESTATUS_FORANEO',
    'ESTATUS2',
    'DIAS_ENTREGADO2',
    'DIAS_ENTREGADO1',
    'ESTATUS3'
  ],
  applyStatusGate: false,
  additionalEditableFields: ['DIAS_ENTREGADO1', 'PEDIDO', 'ESTATUS3', 'OBSERVACION_CHOFER'],
  fieldEditRoles: {
    DIAS_ENTREGADO1: ['cancelaciones', 'cancelacion', 'administrador', 'supervisor'],
    ESTATUS2: ['cancelaciones', 'administrador', 'supervisor', 'seguimientos'],
    ESTATUS3: ['cancelaciones', 'cancelacion', 'administrador', 'supervisor'],
    OBSERVACION_CHOFER: ['chofer', 'supervisor'],
    PEDIDO: ['administrador', 'administradores', 'supervisor', 'facturacion']
  },
  fieldEditRowPredicates: {
    PEDIDO: (row) => isMultiasistenciaAseguradora(row?.ASEGURADORA)
  },
  selectFieldOptions: {
    ...ESTATUS2_SELECT_FIELD_OPTIONS,
    DIAS_ENTREGADO1: ESTATUS_CANCELACION_OPTIONS
  },
  statusQuickTabs: [
    {
      id: 'recuperar',
      label: 'RECUPER CANCELADO',
      filterFn: (row) => hasValue(row?.FECHA_CANCELACION_DE_PIEZA)
    },
    {
      id: 'cambios-por-recuperar',
      label: 'CAMBIOS POR RECUPERAR',
      filterFn: (row) => isCambioPorRecuperarStatus(row) && hasBackOrderDate(row?.BACK_ORDER)
    },
    {
      id: 'recuperar-efectivo',
      label: 'RECUPERAR EFECTIVO',
      filterFn: (row) => normalizeStatusValue(row?.DIAS_ENTREGADO1) === 'recuperar efectivo'
    },
    { id: 'no-surtido', label: 'NO SURTIDO', value: 'no surtido', match: 'equals' },
    { id: 'recuperado', label: 'RECUPERADO', value: 'recuperado', match: 'equals' },
    {
      id: 'por-hacer',
      label: 'POR HACER',
      filterFn: (row) => {
        const status = normalizeStatusValue(row?.DIAS_ENTREGADO1);
        return status === 'recuperar pieza'
          || status === 'recuperar efectivo';
      }
    },
    {
      id: 'resumen-estatus',
      label: 'TODO',
      filterFn: () => true
    }
  ],
  statusQuickTabField: 'DIAS_ENTREGADO1',
  initialStatusQuickTab: 'resumen-estatus',
  dateRangeFieldByStatusQuickTab: {
    'recuperar': CANCELACIONES_DATE_FILTER_FIELD,
    'cambios-por-recuperar': 'BACK_ORDER',
    'recuperar-efectivo': 'BACK_ORDER',
    'no-surtido': CANCELACIONES_DATE_FILTER_FIELD,
    'recuperado': CANCELACIONES_DATE_FILTER_FIELD,
    'por-hacer': CANCELACIONES_DATE_FILTER_FIELD,
    'resumen-estatus': CANCELACIONES_DATE_FILTER_FIELD
  },
  dateRangeFieldLockedByStatusQuickTab: {
    'recuperar': true,
    'cambios-por-recuperar': true,
    'recuperar-efectivo': true
  },
  additionalRowClassRules: cancelacionRowClassRules,
  highlightDuplicateRows: false,
  enablePromesaRowClassRules: false,
  enableCancelacionCounter: true,
  dateRangeField: CANCELACIONES_DATE_FILTER_FIELD,
  dateRangeFieldOptions: [
    {
      value: CANCELACIONES_DATE_FILTER_FIELD,
      label: 'fecha de cancelación',
      startLabel: 'Inicio de fecha de cancelación',
      endLabel: 'Fin de fecha de cancelación'
    },
    {
      value: 'BACK_ORDER',
      label: 'fecha de cambio',
      startLabel: 'Inicio de fecha de cambio',
      endLabel: 'Fin de fecha de cambio'
    }
  ],
  dateRangeLabel: 'fecha de cancelación',
  dateRangeStartLabel: 'Inicio de fecha de cancelación',
  dateRangeEndLabel: 'Fin de fecha de cancelación',
  initialDateRangeStart: '',
  initialDateRangeEnd: '',
  initialDateRangeField: CANCELACIONES_DATE_FILTER_FIELD
});

export default CancelacionesPage;
